import * as React from 'react';

import PlayerSelector from '../PlayerSelector';

import './style.css';

import App from 'src/state/App';
import Room from 'src/state/Room';
import RoomPage from 'src/state/RoomPage';
import Game from 'src/state/Game';
import Player from 'src/state/Player';
import { Ref, RefSet, TwinPair, bothOrNull, RefMap } from '../../util';

export type VState = {
  dragging?: {
    startX: number,
    x: number,
  },
  dragComplete?: boolean,
};

function getVState(page: RoomPage, id: string): VState {
  return page.vStates[id] || {};
}

function setVState(page: RoomPage, id: string, vState: VState): RoomPage.Action {
  return {
    type: 'set-partial',
    data: { vStates: { ...page.vStates, [id]: vState } },
  };
}

const Component: React.SFC<{
  store: App.Store,
  room: Room,
  page: RoomPage,
  data: (
    { type: 'selectable' } |
    { type: 'static', players: TwinPair<Ref<Player>> | 'loading' | null }
  ),
  id: string,
  alt?: boolean,
}> = (
  ({store, room, page, data, id, alt = false}) => {
    const dispatch = store.dispatchCurrentRoomPage;

    const players = (
      data.type === 'selectable' ?
      page.selectedPlayers :
      data.players
    );

    const bothPlayers = (() => {
      if (players === 'loading') {
        return null;
      }

      return players && bothOrNull(players);
    })();

    const vState = getVState(page, id);

    const transition = (() => {
      const { dragging } = vState;

      if (!dragging || !bothPlayers) {
        return 0;
      }

      const dx = dragging.x - dragging.startX;
      const targetDx = 0.25 * window.innerWidth;
      const relDx = Math.max(-1, Math.min(1, dx / targetDx));

      return relDx;
    })();

    const clickable = Math.abs(transition) < 0.1;

    const accent = (alpha: number) => ({
      backgroundColor: (
        bothPlayers ?
        // Equivalent to hsla(340, 83%, 42%/38%, ${alpha}) but iOS doesn't
        // support hsla
        (
          !alt ?
          `rgba(196, 18, 77, ${alpha})` :
          `rgba(177, 16, 70, ${alpha})`
        ) :
        ''
      )
    });

    const className = [
      'game-row',
      data.type,
      ...(alt ? ['alt'] : []),
      ...(vState.dragComplete ? ['drag-complete'] : []),
    ].join(' ');

    function getPlayerText(side: 0 | 1) {
      if (!players) {
        return '';
      }

      if (players === 'loading') {
        return 'Loading...';
      }

      const playerRef = players[side];
      const player = playerRef && RefSet.resolve(room.entities.players, playerRef);
      return (player && player.name) || '';
    }

    return (
      <div
        className={className}
        onTouchStart={e => {
          if (vState.dragComplete) {
            return;
          }

          dispatch(setVState(page, id, {
            dragging: {
              startX: e.touches[0].screenX,
              x: e.touches[0].screenX,
            }
          }));
        }}
        onTouchMove={e => {
          if (vState.dragComplete) {
            return;
          }

          const oldVState = getVState(page, id);

          const { startX } = (
            oldVState.dragging ||
            { startX: e.touches[0].screenX }
          );

          const x = e.touches[0].screenX;

          dispatch(setVState(page, id, {
            dragging: { startX, x },
          }));
        }}
        onTouchEnd={e => {
          if (vState.dragComplete || !bothPlayers) {
            return;
          }

          setTimeout(() => {
            if (Math.abs(transition) > 0.9) {
              dispatch(setVState(page, id, {
                dragging: vState.dragging,
                dragComplete: Math.abs(transition) > 0.9,
              }));

              setTimeout(
                () => {
                  store.dispatchRoom(room.ref, {
                    type: 'add-game',
                    data: Game({
                      players: bothPlayers,
                      outcome: transition < 0 ? 'win' : 'loss',
                    }),
                  });

                  dispatch(setVState(page, id, {}));
                },
                250
              );
            } else {
              dispatch(setVState(page, id, {}));
            }
          });
        }}
      >
        <div style={accent(Math.max(0, -transition))}>
          {
            data.type === 'selectable' ?
            (
              <PlayerSelector
                store={store}
                room={room}
                page={page}
                playerSide={0}
              />
            ) :
            (<div className="player-static">{getPlayerText(0)}</div>)
          }
        </div>
        <div className="v-container">
          <div
            className={`v${bothPlayers ? ' active' : ''}`}
            style={accent(1 - Math.abs(transition))}
            onClick={() => bothPlayers && clickable && RoomPage.showDialog(
              {
                type: 'GameRow',
                players: bothPlayers,
                ratings: bothOrNull(TwinPair.map(bothPlayers, player => RefMap.get(page.animatedRatings, player))),
                scaledRatingDiffs: Room.ScaledRatingDiffs(room, store.getState().calculator, bothPlayers),
              },
              dispatch,
            )}
          >
            v
          </div>
        </div>
        <div style={accent(Math.max(0, transition))}>
          {
            data.type === 'selectable' ?
            (
              <PlayerSelector
                store={store}
                room={room}
                page={page}
                playerSide={1}
              />
            ) :
            (<div className="player-static">{getPlayerText(1)}</div>)
          }
        </div>
      </div>
    );
  }
);

export default Component;



// WEBPACK FOOTER //
// ./src/ui/GameRow/index.tsx